import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  areStationsDragReorderedAtom,
  lastRouteRebuildAtAtom,
  lastSelectedRebuildTypeAtom,
  RideCrudModeKey,
  rideEditHasMetadataChangedAtom,
  rideHasNotPriceRateAtom,
  rideHasSignificantTimeChangeAtom,
  rideHasUnsavedStationChangeAtom,
  rideManualDateChangeInfoAtom,
  ridePassengerDeleteBookingStateConfirmAtom,
  ridePostSaveTransitionAtom,
} from "@ryde/shared";

import { useCrudFormCleanup } from "../dynamic-forms";
import { useRideAreasSnapshot } from "./use-ride-areas-snapshot";
import { useRideMetadataSnapshot } from "./use-ride-metadata-snapshot";

export const useRideFormCleanup = () => {
  const { resetEditFormPreviousData } = useCrudFormCleanup(
    RideCrudModeKey.uiUnit,
    RideCrudModeKey.formMode,
  );

  const setPassDeleteBookingAction = useSetRecoilState(
    ridePassengerDeleteBookingStateConfirmAtom,
  );

  const setRideHasUnsavedStationChange = useSetRecoilState(
    rideHasUnsavedStationChangeAtom,
  );
  const setRideHasSignificantTimeChange = useSetRecoilState(
    rideHasSignificantTimeChangeAtom,
  );
  const setRideEditHasMetadataChanged = useSetRecoilState(
    rideEditHasMetadataChangedAtom,
  );

  const setRideHasNoPriceRate = useSetRecoilState(rideHasNotPriceRateAtom);
  const setRideManualDateChangeInfo = useSetRecoilState(
    rideManualDateChangeInfoAtom,
  );

  const setStationsAreDragReordered = useSetRecoilState(
    areStationsDragReorderedAtom,
  );

  const setLastSelectedRebuildType = useSetRecoilState(
    lastSelectedRebuildTypeAtom,
  );
  const setLastRouteRebuildAt = useSetRecoilState(lastRouteRebuildAtAtom);
  const setRidePostSaveTransition = useSetRecoilState(
    ridePostSaveTransitionAtom,
  );

  const { resetRidePreviousAreasSnapshot } = useRideAreasSnapshot();
  const { resetRidePreviousMetadataSnapshot } = useRideMetadataSnapshot();

  const cleanupRideFormStateAtoms = useEvent((): void => {
    setPassDeleteBookingAction(new Map([]));
    setRideHasUnsavedStationChange(false);
    setRideHasSignificantTimeChange(false);
    setRideEditHasMetadataChanged(false);
    setRideHasNoPriceRate(false);
    setRideManualDateChangeInfo(undefined);
    setStationsAreDragReordered(false);
    setLastSelectedRebuildType(null);
    setLastRouteRebuildAt(null);
    // Also clears ridePostSaveTransitionAtom. Callers that still need the
    // unsaved-changes suppress across a post-save drawer switch (footer save
    // handlers) MUST set ridePostSaveTransition back to true AFTER this cleanup
    // — processApiResponse runs cleanup before returning to those handlers.
    setRidePostSaveTransition(false);
    resetRidePreviousAreasSnapshot();
    resetRidePreviousMetadataSnapshot();
  });

  const fullResetPreviousRideEditData = useEvent((): void => {
    cleanupRideFormStateAtoms();
    resetEditFormPreviousData();
  });
  return { cleanupRideFormStateAtoms, fullResetPreviousRideEditData };
};
